import React, { useState, useEffect, useRef } from "react";
import { Menu, Transition } from "@headlessui/react";
import moment from "moment";
import {
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import DayEvents from "./DayEvents";
import LittleCalendar from "./LittleCalendar";
import { generateDaysArrayForDay, generateDaysArrayForWeek } from "./calenderUtilties";
import ShowInfoAfspraak from "./ShowInfoAfspraak";
import CrudEventCalendly from "./CrudEventCalendly";
import CrudEventCalendlyPatienten from "./CrudEventCalendlyPatienten";

// Utility function for class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Header = ({ date, day, setOpen }) => (
  <header className="flex flex-none items-center justify-between border-b border-wa-border bg-wa-panel px-4 py-3 sm:px-6">
    <div>
      <h1 className="text-lg font-semibold leading-6 text-wa-text">
        <time dateTime="2022-01-22">{date}</time>
      </h1>
      <p className="mt-1 text-sm text-wa-muted">{day}</p>
    </div>
    <div className="flex items-center">
      <div className="hidden md:ml-4 md:flex md:items-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-6 rounded-md bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm hover:bg-wa-accentdeep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wa-accent"
        >
          Afspraak maken
        </button>
      </div>
      <Menu as="div" className="relative ml-6 md:hidden">
        <Menu.Button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-wa-muted hover:text-wa-text">
          <span className="sr-only">Open menu</span>
          <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        </Menu.Button>
        <Transition as={React.Fragment}>
          <Menu.Items className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-wa-border overflow-hidden rounded-md border border-wa-border bg-wa-panel2 shadow-lg focus:outline-none">
            {/* Menu Items Here */}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  </header>
);


const DayNavigation = ({days, containerNav, setDayselected }) => (
  <div
    ref={containerNav}
    className="sticky top-0 z-10 grid flex-none grid-cols-7 bg-wa-panel2 text-xs text-wa-muted shadow ring-1 ring-wa-border md:hidden"
  >
    {days.map((day, index) => (
    <button key={index} onClick={()=>setDayselected(day)} type="button" className="flex flex-col items-center pb-1.5 pt-3">
      <span>{day?.titleDay}</span>
      <span className={classNames("mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-wa-text",
        day?.events?.length>0 ? "bg-wa-accent text-wa-onaccent": "",
        day?.isToday ? "ring-2 ring-wa-accent" :""
      )}>
      {day?.num}
      </span>
    </button>
    ))}
  </div>
);



const TimeSlot = ({ containerOffset }) => {
  const hours = Array.from(
    { length: 15 },
    (_, i) => `${(i + 8).toString().padStart(2, "0")}:00`
  );
  return (
    <div
      className="col-start-1 col-end-2 row-start-1 grid divide-y divide-wa-border"
      style={{ gridTemplateRows: "repeat(15, minmax(1rem, 1fr))" }}
    >
      <div ref={containerOffset} className="row-end-1 h-7"></div>
      {hours.map((hour, index) => (
        <React.Fragment key={index}>
          <div>
            <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-wa-muted">
              <span className="text-sm">{hour}</span>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const EventsArea = ({ selectDate, containerOffset, showAfspraak }) => (
  <div className="grid flex-auto grid-cols-1 grid-rows-1">
    <TimeSlot containerOffset={containerOffset} />
    <DayEvents selectDate={selectDate} showAfspraak={showAfspraak} />
  </div>
);

const DayView = ({ userdetails, user, select, appointments }) => {
  const [open, setOpen] = useState(false);
  const [dayselected, setDayselected] = useState(null);
  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);
  const [currentDate, setCurrentDate] = useState(moment().locale("nl-be")); // Current week starting date

  const [days, setDays] = useState(
    generateDaysArrayForWeek(currentDate, dayselected, appointments)
  );

  const [afspraak, setAfspraak] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const showAfspraak = (afspr) => {
    setAfspraak(afspr);
    setOpenModalEdit(true);
  };

  const formattedMonth = moment().locale("nl-be").format("MMMM DD, YYYY");
  const Daylong = moment().locale("nl-be").format("dddd");

  useEffect(() => {
    const now = new Date();
    const currentMinute = now.getHours() * 60 + now.getMinutes();
    const scrollable =
      container.current.scrollHeight -
      containerNav.current.offsetHeight -
      containerOffset.current.offsetHeight;
    container.current.scrollTop =
      scrollable * Math.max(0, Math.min(1, (currentMinute - 480) / 900));
  }, []);


  return (
    <div className="flex h-full flex-col rounded-lg border border-wa-border bg-wa-panel text-wa-text">
      <Header
        date={
          moment(dayselected?.start_time).format("MMMM DD, YYYY") || formattedMonth
        }
        day={moment(dayselected?.start_time).format("dddd") || Daylong}
        setOpen={setOpen}
      />
      <div className="isolate flex flex-auto overflow-hidden bg-wa-panel2">
        <div ref={container} className="flex flex-auto flex-col overflow-auto">
          <div className="border-b border-wa-border bg-wa-panel2 px-4 py-2 text-xs text-wa-muted md:hidden">
            Kies een dag bovenaan en scroll door de tijdslijn.
          </div>
          <DayNavigation setDayselected={setDayselected} days={days} containerNav={containerNav} />
          <div className="flex w-full flex-auto">
            <div className="w-14 flex-none bg-wa-panel2 ring-1 ring-wa-border" />
            <EventsArea
              selectDate={dayselected}
              containerOffset={containerOffset}
              showAfspraak={showAfspraak}
            />
          </div>
        </div>
        {user?.role == 2 ? (
        <CrudEventCalendly openModel={open} setOpenModel={setOpen} />
      ) : (
        <CrudEventCalendlyPatienten openModel={open} setOpenModel={setOpen} />
      )}
        <div className="hidden border-l border-wa-border bg-wa-panel p-3 xl:block xl:w-80">
          <LittleCalendar
            currentMonth={moment().format("YYYY-MM")}
            generateDaysArray={generateDaysArrayForDay}
            appointments={appointments}
            selectedDay={dayselected}
            setDayselected={setDayselected}
          />
        </div>
         <ShowInfoAfspraak
        open={openModalEdit}
        setOpen={setOpenModalEdit}
        select={select}
        evenment={afspraak}
        user={user}
      />
      </div>
    </div>
  );
};

export default DayView;
